import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Sparkles,
  CheckCircle2,
  Calendar,
  MapPin,
  Clock,
  ArrowRight,
  CreditCard,
  Building2,
  ShieldCheck,
  FileText,
} from 'lucide-react';
import { getParticipantProfile, ParticipantPortalResponse } from '../lib/api';

export const MyIiabWelcomePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const [portalData, setPortalData] = useState<ParticipantPortalResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      sessionStorage.setItem('iiab_portal_token', token.trim());
    }
    if (email) {
      const normalized = email.trim().toLowerCase();
      sessionStorage.setItem('iiab_participant_email', normalized);
      sessionStorage.setItem('iiab_portal_email', normalized);
    }
    if (email || token) {
      setLoading(true);
      getParticipantProfile(email || undefined, token || undefined)
        .then((data) => {
          setPortalData(data);
          if (data?.attendee?.email) {
            const resolvedEmail = data.attendee.email.trim().toLowerCase();
            sessionStorage.setItem('iiab_participant_email', resolvedEmail);
            sessionStorage.setItem('iiab_portal_email', resolvedEmail);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [email, token]);

  const attendee = portalData?.attendee;
  const event = portalData?.event || {
    name: 'India International Agri Business Conference & Expo (IIAB 2027)',
    dates: '27–28 July 2027',
    venue: 'Jio World Convention Centre, Pavilion 3, Bandra Kurla Complex (BKC), Mumbai',
  };

  const portalQuery = new URLSearchParams();
  if (token) portalQuery.set('token', token);
  if (email || attendee?.email) portalQuery.set('email', (email || attendee?.email || '').trim());
  const portalHref = portalQuery.toString() ? `/my-iiab?${portalQuery.toString()}` : '/my-iiab';

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col items-center justify-center p-4 py-12">
      <div className="max-w-xl w-full bg-slate-800/90 border border-slate-700/80 rounded-3xl p-8 sm:p-10 shadow-2xl backdrop-blur-md relative overflow-hidden">
        {/* Glow decoration */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />

        {/* Header Icon */}
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0A3D27] to-emerald-700 flex items-center justify-center p-3 shadow-lg shadow-emerald-950/40 mb-6 border border-emerald-500/20 mx-auto">
          <CheckCircle2 className="w-8 h-8 text-[#FAF5EA]" />
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-400 bg-emerald-950/80 border border-emerald-800 px-3 py-1 rounded-full">
            Registration Confirmed
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight mt-3">
            Welcome to IIAB 2027!
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            Your registration is confirmed. We look forward to seeing you at Asia's premier agribusiness convergence.
          </p>
        </div>

        {/* Confirmation Summary Card */}
        <div className="bg-slate-900/80 rounded-2xl p-5 border border-slate-700/80 mb-6 space-y-3 text-xs">
          {attendee && (
            <div className="flex justify-between pb-3 border-b border-slate-800">
              <span className="text-slate-400">Attendee:</span>
              <span className="font-bold text-white text-right">{attendee.fullName}</span>
            </div>
          )}
          {attendee?.companyName && (
            <div className="flex justify-between pb-3 border-b border-slate-800">
              <span className="text-slate-400">Organization:</span>
              <span className="font-semibold text-slate-300 text-right">{attendee.companyName}</span>
            </div>
          )}
          {attendee?.collectionCode && (
            <div className="flex justify-between pb-3 border-b border-slate-800">
              <span className="text-slate-400">Desk Collection Code:</span>
              <span className="font-mono font-black text-[#E6C687] tracking-widest text-right">
                {attendee.collectionCode}
              </span>
            </div>
          )}
          <div className="flex justify-between pb-3 border-b border-slate-800">
            <span className="text-slate-400">Dates:</span>
            <span className="font-semibold text-emerald-400">27–28 July 2027 (10:00 AM – 06:00 PM)</span>
          </div>
          <div className="flex justify-between items-start">
            <span className="text-slate-400 shrink-0">Venue:</span>
            <span className="font-medium text-slate-300 text-right pl-4">
              Jio World Convention Centre, Pavilion 3, BKC, Mumbai
            </span>
          </div>
        </div>

        {/* Badge Collection Notice */}
        <div className="p-4 rounded-2xl bg-amber-950/40 border border-amber-800/60 text-amber-200 text-xs mb-8 leading-relaxed">
          <div className="flex items-center gap-2 font-bold text-amber-300 mb-1">
            <CreditCard className="w-4 h-4" />
            <span>Badge Collection at Venue</span>
          </div>
          Present your 6-character collection code + photo ID at the Registration Desk in Pavilion 3 to collect your physical 4×6 QR &amp; NFC Badge.
        </div>

        {/* Call to Actions */}
        <div className="space-y-3">
          <Link
            to={portalHref}
            className="w-full bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold py-3.5 px-4 rounded-xl shadow-lg shadow-emerald-950/50 flex items-center justify-center gap-2 transition-all cursor-pointer text-sm"
          >
            <span>Open My IIAB Portal</span>
            <ArrowRight className="w-4 h-4" />
          </Link>

          <Link
            to="/floorplan"
            className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-medium py-3 px-4 rounded-xl border border-slate-700 flex items-center justify-center gap-2 transition-colors text-xs"
          >
            <span>Explore Floor Plan &amp; Exhibitors</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MyIiabWelcomePage;
